
// Keeps every plan paired with its phone.
// Each source asks for a check; close asks merge.
document.addEventListener("DOMContentLoaded", function () {
  const GZ = window.GzTheme;
  if (!GZ || GZ.cartSync) return;

  GZ.CART_SYNC_MERGE_MS = GZ.CART_SYNC_MERGE_MS ?? 150;
  // Delay of the second check after an interrupted change.
  GZ.FOLLOW_UP_CHECK_MS = GZ.FOLLOW_UP_CHECK_MS ?? 4000;
  // Tab switches re-check at most this often.
  const RETURN_GAP_MS = 20000;
  // Pages replaced in the same window go quiet.
  const isCurrentPage = () => window.GzTheme === GZ;
  const tabId = GZ.generateUUID().slice(0, 8);
  const reasons = new Set();
  let channel = null;
  let mergeTimer = null;
  let followUpTimer = null;
  let running = null;
  let refreshWanted = false;
  let lastResyncAt = 0;

  // Stock per cart line, as Shopify rendered it.
  const snapshotStock = () => Object.fromEntries(
    (GZ.CART_SNAPSHOT || []).filter((line) => Number.isFinite(line.stock)).map((line) => [line.key, line.stock])
  );

  // Stock per cart line, read from the quantity inputs.
  // The max never drops below 1, so data-stock helps.
  const cartPageStock = () => {
    const stock = {};
    document.querySelectorAll('gz-cart-item input[name="quantity"]').forEach((input) => {
      const limits = [parseInt(input.dataset.stock, 10), parseInt(input.max, 10)].filter(Number.isFinite);
      if (input.dataset.itemKey && limits.length) stock[input.dataset.itemKey] = Math.min(...limits);
    });
    return stock;
  };

  // True if the cart Shopify rendered already needs repairs.
  const isSnapshotBroken = () => {
    const { updates, untag } = GZ.planRepairs(GZ.CART_SNAPSHOT || [], snapshotStock());
    return Object.keys(updates).length > 0 || untag.length > 0;
  };

  // Runs one merged check; redraws the cart page if asked.
  // Then repairs the cart and tells the page what happened.
  const runCheck = async () => {
    const reason = [...reasons].join(", ");
    const isCartPage = GZ.isCartPage();
    const shouldRefresh = refreshWanted && isCartPage;
    reasons.clear();
    refreshWanted = false;

    try {
      if (shouldRefresh) {
        await GZ.withCartLock(() => GZ.refreshCartSections()).catch(() => null);
      }
      const response = await GZ.checkCart(isCartPage ? cartPageStock() : snapshotStock(), reason);
      if (response && isCartPage) GZ.renderCartSections(response);
      document.dispatchEvent(new CustomEvent("gz:cart-sync", { detail: { reason, repaired: !!response } }));
    } catch (error) {
      GZ.reportError("cart check failed", error, { reason, location: window.location.href });
    }
  };

  // Starts a check unless one runs; later asks run after.
  const startCheck = () => {
    if (running) return;
    running = runCheck().finally(() => {
      running = null;
      if (isCurrentPage() && reasons.size && !mergeTimer) startCheck();
    });
  };

  // Asks for a check soon; asks close together become one.
  // With refresh, the cart page is redrawn first.
  const requestCheck = (reason, { immediate = false, refresh = false } = {}) => {
    if (!isCurrentPage()) return;
    reasons.add(reason);
    refreshWanted = refreshWanted || refresh;
    clearTimeout(mergeTimer);
    mergeTimer = null;
    if (immediate) return startCheck();
    mergeTimer = setTimeout(() => {
      mergeTimer = null;
      if (isCurrentPage()) startCheck();
    }, GZ.CART_SYNC_MERGE_MS);
  };

  // Re-checks the cart page, at most once per minGapMs.
  const resyncCartPage = (reason, { minGapMs = 0 } = {}) => {
    if (!GZ.isCartPage() || Date.now() - lastResyncAt < minGapMs) return;
    lastResyncAt = Date.now();
    requestCheck(reason, { refresh: true });
  };

  GZ.cartSync = {
    requestCheck,
    // Resolves when no check is waiting or running.
    idle: async () => {
      while (mergeTimer || followUpTimer || running) await (running || new Promise((resolve) => setTimeout(resolve, GZ.CART_SYNC_MERGE_MS)));
    },
  };

  // Tells other tabs and the page the cart changed.
  GZ.publishCartChange = () => {
    try { channel?.postMessage({ type: "changed", tab: tabId }); } catch (e) { /* ignore */ }
    document.dispatchEvent(new CustomEvent("gz:cart-changed", { detail: { source: "theme" } }));
  };

  // Listens for cart changes made in other tabs.
  // An open channel blocks the browser's back/forward cache.
  const openChannel = () => {
    if (typeof BroadcastChannel !== "function" || channel) return;
    channel = new BroadcastChannel("gz-cart");
    channel.onmessage = ({ data }) => {
      if (!isCurrentPage() || data?.tab === tabId) return;
      if (GZ.isCartPage()) resyncCartPage("cart changed in another tab");
      else requestCheck("cart changed in another tab");
    };
  };

  // Closes the channel so the back/forward cache works.
  const closeChannel = () => { channel?.close(); channel = null; };

  openChannel();
  window.addEventListener("pagehide", () => { if (isCurrentPage()) closeChannel(); });

  // True for cart requests not made by our code.
  const isForeignCartRequest = (entry) =>
    (entry.initiatorType === "fetch" || entry.initiatorType === "xmlhttprequest") &&
    /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(entry.name) &&
    !entry.name.includes(GZ.OWN_REQUEST_MARK);

  // Re-checks the cart when another script changes it.
  if (typeof PerformanceObserver === "function") {
    try {
      new PerformanceObserver((list) => {
        if (!isCurrentPage()) return;
        const foreign = list.getEntries().filter(isForeignCartRequest);
        if (!foreign.length) return;
        document.dispatchEvent(new CustomEvent("gz:cart-changed", { detail: { source: "foreign" } }));
        const reason = "cart changed by another script";
        if (GZ.isCartPage()) resyncCartPage(reason); else requestCheck(reason);
      }).observe({ type: "resource", buffered: false });
    } catch (e) { /* not supported */ }
  }

  // Reports checkout pressed with a broken cart or running change.
  // Bound once, even if the script runs again.
  if (!window.__gzCheckoutReportBound) {
    window.__gzCheckoutReportBound = true;
    document.addEventListener("click", (event) => {
      const isCheckout = event.target.closest?.('#checkout, button[name="checkout"], .cart__dynamic-checkout-buttons');
      if (!isCurrentPage() || !isCheckout) return;
      const cart = GZ.cartSummary(GZ.CART_DATA?.items || GZ.CART_SNAPSHOT);
      const changeRunning = GZ._cartBusy > 0;
      if (changeRunning || !cart.pairing_ok) GZ.reportError("checkout clicked with an unsafe cart", null, { change_running: changeRunning, cart });
    }, true);
  }

  // Re-checks when the customer returns to the cart.
  window.addEventListener("pageshow", (event) => {
    if (!isCurrentPage() || !event.persisted) return;
    openChannel();
    resyncCartPage("restored from history");
  });
  document.addEventListener("visibilitychange", () => {
    if (isCurrentPage() && document.visibilityState === "visible") resyncCartPage("tab revisited", { minGapMs: RETURN_GAP_MS });
  });
  window.addEventListener("online", () => {
    if (isCurrentPage()) resyncCartPage("network is back", { minGapMs: RETURN_GAP_MS });
  });

  // On every page load: check the rendered cart in memory.
  // A healthy cart costs no requests; broken ones are repaired.
  const snapshotBroken = isSnapshotBroken();
  const wasInterrupted = GZ.wasChangeInterrupted();
  if (wasInterrupted || snapshotBroken) {
    requestCheck(wasInterrupted ? "previous page left during a cart change" : "cart rendered by the store is out of sync", { immediate: true });
  }
  if (wasInterrupted) {
    followUpTimer = setTimeout(() => {
      followUpTimer = null;
      requestCheck("checking again after an interrupted change", { immediate: true });
    }, GZ.FOLLOW_UP_CHECK_MS);
  }
});
